import { FormGroup } from '@angular/forms';

// TODO: WIP
export const handleChangesWithThrottling = (
  timeout: NodeJS.Timeout,
  cb: (arg?: any) => void,
  delay: number
) => {
  if (timeout !== null) {
    clearTimeout(timeout);
  }
  timeout = setTimeout(() => {
    cb();
    timeout = null;
  }, delay);
};

export const sumNumbers = (numbers: number[]) => {
  return numbers.reduce(
    (accumulator, currentValue) => accumulator + currentValue,
    0
  );
};

export const delay = async (ms: number): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms));

export const isValueInArray = <T>(arr: T[], value: T) =>
  arr.find((x) => x === value) !== undefined;
export const normalDistributionPDF = (
  x: number,
  mean: number,
  deviation: number
) => {
  const coefficient = 1 / (deviation * Math.sqrt(2 * Math.PI));
  const exponent = -((x - mean) ** 2) / (2 * deviation ** 2);
  return coefficient * Math.exp(exponent);
};

export class ScrollToElementHelper {
  private _el: HTMLElement | null = null;
  private pageHeaderHeight = 80;

  public async scroll(selector: string, delayMs: number | null = null) {
    if (delayMs !== null) {
      await delay(delayMs);
    }

    this._el = this.findElement(selector);

    if (this._el === null) {
      return;
    }

    const domRect = this.getElRect();
    if (domRect === null) {
      return;
    }

    window.scrollBy({
      top: domRect.top - this.pageHeaderHeight,
      left: 0,
      behavior: 'auto',
    });
  }

  private getElRect(): DOMRect | null {
    if (this._el === null) {
      return null;
    }
    return this._el.getBoundingClientRect();
  }

  private findElement(selector: string): HTMLElement | null {
    return document.querySelector(selector);
  }
}

export const generateGUID = () =>
  'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    // eslint-disable-next-line no-bitwise
    var r = (Math.random() * 16) | 0;
    // eslint-disable-next-line no-bitwise
    var v = c === 'x' ? r : (r & 0x3) | 0x8;

    return v.toString(16);
  });

export const randomNumberInRange = (min: number, max: number) =>
  Math.floor(Math.random() * (max - min + 1) + min);

export const tryParseInt = (
  value: string,
  defaultValue = null
): number | null => {
  const parsedValue = parseInt(value, 10);

  if (isNaN(parsedValue)) {
    return defaultValue;
  }
  return parsedValue;
};

export const findInvalidControlsInFormGroup = (formGroup: FormGroup) => {
  const invalid = [];
  const controls = formGroup.controls;
  for (const name in controls) {
    if (controls[name].invalid) {
      invalid.push(name);
    }
  }
  return invalid;
};

export const validateForm = (form: FormGroup | undefined) => {
  if (form === undefined) {
    return false;
  }

  if (form.invalid) {
    form.markAllAsTouched();
    return false;
  }

  return true;
};

declare global {
  interface File {
    toBaseSixtyFour(): Promise<string>;
    toFileUploadRequest(): Promise<any>;
  }
}

Object.defineProperty(File.prototype, 'toBaseSixtyFour', {
  enumerable: false,
  writable: false,
  configurable: false,
  value: function toBaseSixtyFour(this: File): Promise<string> {
    return new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(this);
      reader.onload = () => {
        resolve(reader.result?.toString() ?? '');
      };
      reader.onerror = (error) => reject(error);
    });
  },
});

Object.defineProperty(File.prototype, 'toFileUploadRequest', {
  enumerable: false,
  writable: false,
  configurable: false,
  value: function toFileUploadRequest(this: File) {
    return new Promise((resolve, reject) => {
      this.toBaseSixtyFour()
        .then((data) => {
          const nameArr = this.name.split('.');
          const extension = '.' + nameArr[nameArr.length - 1] ?? '';
          nameArr.pop();
          const name = nameArr.join('.');

          resolve({
            name,
            contentType: this.name.endsWith('.msg')
              ? 'application/vnd.ms-outlook'
              : this.type,
            extension,
            data,
          });
        })
        .catch((e) => reject(e));
    });
  },
});

export const openBlobInNewWindow = (data: Blob) => {
  const url = window.URL.createObjectURL(data);
  window.open(url, '_blank');
};

export const dynamicSort = (property: string) => {
  var sortOrder = 1;

  if (property[0] === '-') {
    sortOrder = -1;
    property = property.substring(1);
  }

  return (a: any, b: any) => {
    if (sortOrder === -1) {
      return b[property].localeCompare(a[property]);
    } else {
      return a[property].localeCompare(b[property]);
    }
  };
};

export const filterAsync = async <T>(arr: T[], callback: any): Promise<T[]> => {
  const fail = Symbol();
  return (
    await Promise.all(
      arr.map(async (item: any) => ((await callback(item)) ? item : fail))
    )
  ).filter((i) => i !== fail);
};

export const groupBy = <T, K extends keyof any>(
  list: T[],
  getKey: (item: T) => K
) =>
  list.reduce((previous, currentItem) => {
    const group = getKey(currentItem);

    if (!previous[group]) {
      previous[group] = [];
    }

    previous[group].push(currentItem);
    return previous;
  }, {} as Record<K, T[]>);

export const isNullUndefinedOrEmptyString = (str: string | null | undefined) =>
  str === null || str === undefined || str.trim() === '';

export const convertToBoolean = (input: string): boolean | undefined => {
  try {
    return JSON.parse(input.toLowerCase());
  } catch (e) {
    return undefined;
  }
};

export const formatDate = (date: Date, format: string) => {
  const map: any = {
    mm: date.getMonth() + 1,
    dd: date.getDate(),
    yyyy: date.getFullYear(),
  };

  return format.replace(/mm|dd|yyyy/gi, (matched) => map[matched]);
};

export const swapElements = (array: any[], index1: number, index2: number) => {
  array[index1] = array.splice(index2, 1, array[index1])[0];
};

export const convertToRands = (num: number) =>
  Intl.NumberFormat('en-ZA', {
    style: 'currency',
    currency: 'ZAR',
  }).format(num);

export const convertStringToHTML = (str: string) => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(str, 'text/html');
  return doc.body;
};

export const positionInAlphabet = (text: string, position: number = 0) =>
  text.charCodeAt(position) - 64;

export const daysBetween = (a: Date = new Date(), b: Date = new Date()) => {
  const _MS_PER_DAY = 1000 * 60 * 60 * 24;
  // Discard the time and time-zone information.
  const utc1 = Date.UTC(a.getFullYear(), a.getMonth(), a.getDate());
  const utc2 = Date.UTC(b.getFullYear(), b.getMonth(), b.getDate());

  return Math.floor((utc2 - utc1) / _MS_PER_DAY);
};

// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
export type ExtendedProp = { [key: string]: any };
export type ExtendedPropObject<T> = T & ExtendedProp;

/**
 * Adds extra properites to the specified object and returns the resulting object.
 *
 * @param data The data to which the custom properties must be added.
 * @param customProps An object containing the custom properties to add to the data.
 * @returns An object with the combined properties of the data and customProps objects.
 */
export const extendObjectProperties = <T>(
  data: T,
  customProps: ExtendedProp
): ExtendedPropObject<T> => {
  const mappedObject = data as ExtendedPropObject<T>;
  Object.keys(customProps).forEach((key) => {
    // map each key to its own object with a value and assign
    const prop: ExtendedProp = {};
    prop[key] = customProps[key];
    Object.assign(mappedObject, prop);
  });

  return mappedObject;
};

/**
 * Adds extra properites to the specified objects and returns the resulting objects as an array.
 *
 * @param data The data array to which the custom properties must be added.
 * @param customProps An object containing the custom properties to add to the data.
 * @returns An array with the combined properties of the data and customProps objects.
 */
export const extendObjectsProperties = <T>(
  data: T[],
  customProps: ExtendedProp
): ExtendedPropObject<T>[] =>
  data.map((obj) => extendObjectProperties(obj, customProps));

export const clearFileInput = (ctrl: HTMLInputElement) => {
  try {
    ctrl.value = '';
  } catch (ex) {}
  if (ctrl.value && ctrl.value.length > 0 && ctrl.parentNode) {
    ctrl.parentNode.replaceChild(ctrl.cloneNode(true), ctrl);
  }
};

export const isInPast = (time: Date | undefined) =>
  time === undefined ? false : time < new Date();
